import { useEffect, useRef, useState } from 'react';
import { type MapProvider, getPreferredMap } from '../api';
import { t } from '../i18n';

/**
 * "Navigate to address" component.
 *
 * Replaces the old row-of-three-grey-buttons design (Google / Waze / Apple
 * as equal-weight chips) — staff reported it "looks like a row of bookmarks,
 * not a navigation prompt". A driver wants to drive; the UI now leads with
 * a single primary "Navigate" CTA wired to the user's saved-default map,
 * with the other two demoted to 36px icon-only buttons in a secondary row.
 *
 * Variants:
 *   - "full"     used on /op/:id sidebar and StaffToday cards. Light-gold
 *                card, address label, full-width primary CTA (56px on
 *                mobile), tiny icon row beneath for the other two apps.
 *   - "compact"  used on the OwnerTasks list. Single inline pill that opens
 *                the default map on click, with a <details> popover that
 *                reveals the other two on long-press / right-click.
 *
 * Deep-link URL formats are stable per each app's documentation and
 * deliberately match the previous component's encoding so existing tests /
 * muscle-memory don't break:
 *   - Google Maps:  https://www.google.com/maps/search/?api=1&query=<addr>
 *   - Waze:         https://waze.com/ul?q=<addr>&navigate=yes
 *   - Apple Maps:   https://maps.apple.com/?q=<addr>
 *
 * No new npm packages. The brand SVG marks are drawn inline below as
 * 24×24 paths inspired by each app's marketing assets, simplified down to
 * a single recognizable glyph (G / wave / pin). They live in this file so
 * the only thing this component depends on is React + i18n.
 */
export function LocationLinks({
  address,
  variant = 'full',
}: {
  address: string | null | undefined;
  variant?: 'full' | 'compact';
}) {
  if (!address || !address.trim()) return null;
  const addr = address.trim();
  const links = buildLinks(addr);
  const preferred = getPreferredMap();
  const primary = links[preferred];

  if (variant === 'compact') {
    return <CompactPill address={addr} primary={primary} links={links} preferred={preferred} />;
  }
  return <FullCard address={addr} primary={primary} links={links} preferred={preferred} />;
}

interface Links {
  google: { href: string; label: string };
  waze: { href: string; label: string };
  apple: { href: string; label: string };
}

/**
 * Where this address opens, in the map the reader chose.
 *
 * His staff card shows the location as a line of text rather than a card, and
 * a driver reading "Main Villa, Level 1" still wants to press it. This is the
 * same deep link the full card's button uses — one definition, not two.
 */
export function mapHref(address: string | null | undefined): string | undefined {
  if (!address || !address.trim()) return undefined;
  return buildLinks(address.trim())[getPreferredMap()].href;
}

function buildLinks(address: string): Links {
  const q = encodeURIComponent(address);
  return {
    google: {
      href: `https://www.google.com/maps/search/?api=1&query=${q}`,
      label: t('Open in Google Maps'),
    },
    waze: {
      href: `https://waze.com/ul?q=${q}&navigate=yes`,
      label: t('Open in Waze'),
    },
    apple: {
      href: `https://maps.apple.com/?q=${q}`,
      label: t('Open in Apple Maps'),
    },
  };
}

function FullCard({
  address,
  primary,
  links,
  preferred,
}: {
  address: string;
  primary: { href: string; label: string };
  links: Links;
  preferred: MapProvider;
}) {
  // The two "other" providers shown as icon-only buttons. Order is stable:
  // whichever isn't the user's default. Keeps muscle memory across users.
  const others: MapProvider[] = (['google', 'waze', 'apple'] as const).filter(
    (m) => m !== preferred,
  );
  // His card, not the old gold-on-ink one: squircle, card white, the address
  // on the left and the way there as a round button on the right — the shape
  // he drew for Location on the task page.
  return (
    <div className="space-y-4 rounded-[2.5rem] border border-border bg-card p-6">
      <div className="flex items-center justify-between gap-4">
        <div className="min-w-0 space-y-1">
          <div className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground">
            {t('Address')}
          </div>
          <div className="line-clamp-2 text-sm font-bold leading-snug" dir="auto">
            {address}
          </div>
        </div>
        <a
          href={primary.href}
          target="_blank"
          rel="noreferrer noopener"
          className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-primary text-white shadow-lg transition-transform active:scale-90"
          aria-label={`${t('Navigate')} — ${primary.label}`}
        >
          <NavIcon className="h-5 w-5" />
        </a>
      </div>
      <div className="flex items-center gap-2 border-t border-border pt-4">
        <span className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground">
          {t('Open in…')}
        </span>
        {others.map((m) => (
          <IconLink key={m} provider={m} href={links[m].href} label={links[m].label} />
        ))}
      </div>
    </div>
  );
}

function CompactPill({
  address,
  primary,
  links,
  preferred,
}: {
  address: string;
  primary: { href: string; label: string };
  links: Links;
  preferred: MapProvider;
}) {
  const [open, setOpen] = useState(false);
  const pressTimer = useRef<number | null>(null);
  const longPressed = useRef(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const truncated = address.length > 32 ? `${address.slice(0, 32)}…` : address;
  const others: MapProvider[] = (['google', 'waze', 'apple'] as const).filter(
    (m) => m !== preferred,
  );

  // Close the popover on outside click — without any deps. We can't use a
  // <details> element here because the long-press has to open the same
  // popover that right-click opens, and <details> doesn't expose that.
  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent) {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  function startPress() {
    longPressed.current = false;
    pressTimer.current = window.setTimeout(() => {
      longPressed.current = true;
      setOpen(true);
    }, 500);
  }
  function cancelPress() {
    if (pressTimer.current !== null) {
      window.clearTimeout(pressTimer.current);
      pressTimer.current = null;
    }
  }

  return (
    <div ref={wrapRef} className="relative inline-flex">
      <a
        href={primary.href}
        target="_blank"
        rel="noreferrer noopener"
        onContextMenu={(e) => {
          e.preventDefault();
          setOpen((v) => !v);
        }}
        onPointerDown={startPress}
        onPointerUp={cancelPress}
        onPointerLeave={cancelPress}
        onClick={(e) => {
          // If the long-press fired, suppress the click that would otherwise
          // also navigate away from the page.
          if (longPressed.current) {
            e.preventDefault();
            longPressed.current = false;
          }
        }}
        className="inline-flex max-w-full items-center gap-1.5 rounded-full bg-muted px-3 py-1.5 text-xs font-medium text-primary hover:bg-secondary transition-colors"
        aria-label={`${t('Navigate')} — ${address}`}
      >
        <NavIcon className="h-3.5 w-3.5 shrink-0" />
        <span className="truncate" dir="auto">
          {truncated}
        </span>
        <span aria-hidden className="text-muted-foreground">
          ·
        </span>
        <span className="shrink-0">{t('Navigate')}</span>
      </a>
      {open && (
        <div className="absolute z-20 top-full mt-1 start-0 flex gap-1.5 rounded-lg border border-border bg-white p-2 shadow-lg">
          {others.map((m) => (
            <IconLink key={m} provider={m} href={links[m].href} label={links[m].label} />
          ))}
        </div>
      )}
    </div>
  );
}

function IconLink({
  provider,
  href,
  label,
}: {
  provider: MapProvider;
  href: string;
  label: string;
}) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer noopener"
      title={label}
      aria-label={label}
      className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-border bg-white text-foreground/80 transition-colors hover:border-primary hover:bg-muted"
    >
      <ProviderIcon provider={provider} className="h-5 w-5" />
    </a>
  );
}

/**
 * Inline 24×24 brand glyphs. Hand-simplified from each provider's public
 * marketing marks (Google Maps pin, Waze speech bubble, Apple Maps pin).
 * No SDKs, no remote fetches, no licensed brand assets — just recognizable
 * silhouettes so a staff member can pick the right app at a glance.
 */
function ProviderIcon({ provider, className }: { provider: MapProvider; className?: string }) {
  if (provider === 'google') return <GoogleMapsIcon className={className} />;
  if (provider === 'waze') return <WazeIcon className={className} />;
  return <AppleMapsIcon className={className} />;
}

function NavIcon({ className }: { className?: string }) {
  // Generic "directions" arrow — used inside the primary CTA so the meaning
  // ("this takes you somewhere") is obvious even without reading the label.
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M21.71 11.29l-9-9a1 1 0 0 0-1.42 0l-9 9a1 1 0 0 0 0 1.42l9 9a1 1 0 0 0 1.42 0l9-9a1 1 0 0 0 0-1.42zM14 14.5V12h-4v3H8v-4a1 1 0 0 1 1-1h5V7.5l3.5 3.5z" />
    </svg>
  );
}

function GoogleMapsIcon({ className }: { className?: string }) {
  // Pin silhouette in Google's red-ish hue.
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
      <path
        d="M12 2C7.58 2 4 5.58 4 10c0 5.5 8 12 8 12s8-6.5 8-12c0-4.42-3.58-8-8-8z"
        fill="#EA4335"
      />
      <circle cx="12" cy="10" r="3" fill="#fff" />
    </svg>
  );
}

function WazeIcon({ className }: { className?: string }) {
  // Rounded speech-bubble pin in Waze blue.
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
      <path
        d="M12 3a8 8 0 0 0-8 8v3.5A2.5 2.5 0 0 0 6.5 17H8l-1 3 4-3h2a8 8 0 0 0 0-16z"
        fill="#33CCFF"
      />
      <circle cx="9" cy="11" r="1.2" fill="#0E1F36" />
      <circle cx="15" cy="11" r="1.2" fill="#0E1F36" />
      <path
        d="M9.5 13.5c.7.8 1.6 1.2 2.5 1.2s1.8-.4 2.5-1.2"
        stroke="#0E1F36"
        strokeWidth="1"
        fill="none"
        strokeLinecap="round"
      />
    </svg>
  );
}

function AppleMapsIcon({ className }: { className?: string }) {
  // Pin with compass arrow in Apple's grey/red marker style.
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
      <circle cx="12" cy="12" r="10" fill="#F5F5F7" stroke="#D2D2D7" />
      <path d="M12 6l3 9-3-1.5L9 15z" fill="#FF3B30" />
    </svg>
  );
}
